/* formSecurity.js
   ----------------------------------------------------------------------
   Tiny helpers shared by every lead-capture form on the site.
   - honeypot: a hidden field bots happily fill out. If non-empty on
     submit, we pretend success but write nothing.
   - cleanField / cleanEmail / cleanPhone: per-field trim + length cap +
     control-char strip. The same caps are enforced server-side in
     firestore.rules. */

/* Strip ASCII control characters (0x00-0x1F except \t \n, plus 0x7F).
   Prevents header injection if a future Trigger Email extension
   concatenates field values into mail headers. Built via RegExp() so
   the escape sequences are stable across tooling that mangles literal
   control bytes in source. */
const CONTROL_CHARS_RE = new RegExp("[\\x00-\\x08\\x0B\\x0C\\x0E-\\x1F\\x7F]", "g");

export function cleanField(value, maxLen) {
  if (value == null) return "";
  const trimmed = String(value).replace(CONTROL_CHARS_RE, "").trim();
  return trimmed.length > maxLen ? trimmed.slice(0, maxLen) : trimmed;
}

/* Loose email regex: presence of an @ and at least one dot in the
   domain. We don't try to validate the full RFC 5322 grammar -- we
   just want to catch obvious typos and reject bot garbage like
   "12345" or "asdfgh". */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function cleanEmail(value, maxLen = 200) {
  const v = cleanField(value, maxLen).toLowerCase();
  return { value: v, valid: EMAIL_RE.test(v) };
}

/* Phone: keep digits, plus, spaces, dashes, parens. Reject anything
   that has fewer than 7 or more than 20 such characters. Permissive
   for international and Dubai formats. */
const PHONE_KEEP_RE = /[^\d+\s()\-]/g;
const PHONE_LEN_RE  = /^[\d+\s()\-]{7,20}$/;

export function cleanPhone(value, maxLen = 40) {
  const v = cleanField(value, maxLen).replace(PHONE_KEEP_RE, "");
  return { value: v, valid: PHONE_LEN_RE.test(v) };
}

/* Honeypot. If the hidden field is non-empty when the form is
   submitted, a bot filled it. Reject silently so the bot thinks it
   succeeded and doesn't retry with a different strategy. */
export function isHoneypotTripped(value) {
  return typeof value === "string" && value.trim().length > 0;
}
